import React, { useState, useEffect, useCallback } from "react";
import {
  UserCheck,
  Play,
  Clock,
  Send,
  CheckCircle,
  AlertCircle,
  Eye,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
  Layers,
} from "lucide-react";
import toast from "react-hot-toast";
import LoaderService from "../services/LoaderService";
import {
  GetPendingVerifications,
  GetPublishMultipleService,
  GetJobDetails,
  GetCompatibilityCheck,
} from "./apis/get";
import { PostTriggerServices, PostServiceJob } from "./apis/post";

interface PendingVerification {
  UserId: string;
  Name: string;
  City: string;
  State: string;
  MaheshwariScore: number;
  Status: string;
  CreatedAt: string;
  UpdatedAt: string;
}

interface JobAttempt {
  id: number;
  job_id: number;
  attempt_no: number;
  status: string;
  worker_id: string;
  started_at: string;
  finished_at: string | null;
  error_msg: string | null;
  meta: any;
}

interface PublishJob {
  id: number;
  service_name: string;
  user_id: string;
  candidate_user_id: string;
  status: string;
  payload: any;
  priority: number;
  max_attempts?: number;
  created_by?: string;
  attempt_statistics?: {
    total_attempts: number;
    failed_attempts: number;
    completed_attempts: number;
    processing_attempts: number;
    first_attempt_at: string;
    last_attempt_at: string;
  };
  attempts?: JobAttempt[];
  error_msg?: string;
  created_at: string;
  completed_at?: string;
  result?: any;
}

const AdminServices = () => {
  // Caste Verification State
  const [pendingVerifications, setPendingVerifications] = useState<
    PendingVerification[]
  >([]);
  const [userIdInput, setUserIdInput] = useState("");
  const [pendingSearch, setPendingSearch] = useState("");
  const [pendingCity, setPendingCity] = useState("");
  const [pendingStateFilter, setPendingStateFilter] = useState("");
  const [pendingStatus, setPendingStatus] = useState("");
  const [pendingScoreMin, setPendingScoreMin] = useState("");
  const [pendingScoreMax, setPendingScoreMax] = useState("");
  const [triggeredRows, setTriggeredRows] = useState<Set<string>>(new Set());
  const [pendingPage, setPendingPage] = useState(1);
  const [totalPendingCount, setTotalPendingCount] = useState(0);
  const [filterKey, setFilterKey] = useState(0);
  const pendingPerPage = 10;

  // Publish Jobs State
  const [publishJobs, setPublishJobs] = useState<PublishJob[]>([]);
  const [totalJobs, setTotalJobs] = useState(0);
  const [jobPage, setJobPage] = useState(1);
  const [jobStatusFilter, setJobStatusFilter] = useState("");
  const [jobServiceFilter, setJobServiceFilter] = useState("");
  const [jobUserIdSearch, setJobUserIdSearch] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [selectedJob, setSelectedJob] = useState<PublishJob | null>(null);
  const jobsPerPage = 10;

  // Service Trigger State
  const [selectedService, setSelectedService] = useState("common_service");
  const [serviceUserId, setServiceUserId] = useState("");
  const [serviceCandidateId, setServiceCandidateId] = useState("");
  const [restartingJobId, setRestartingJobId] = useState<number | null>(null);

  // Compatibility mismatch state
  const [compatibilityWarning, setCompatibilityWarning] = useState<{
    mismatches: { field: string; reason: string }[];
    user: { user_id: string; name: string; age: number | null; marital_status: string | null; gotra?: string | null; maternal_gotra?: string | null };
    candidate: { user_id: string; name: string; age: number | null; marital_status: string | null; gotra?: string | null; maternal_gotra?: string | null };
    pendingPayload: any;
  } | null>(null);

  const dualParamServices = [
    "force_match",
    "hard_match",
    "publish_match",
    "matchmaking_queue_single",
  ];

  const fetchPendingVerifications = useCallback(async (
    page: number,
    search: string,
    city: string,
    stateFilter: string,
    status: string,
    scoreMin: string,
    scoreMax: string,
  ) => {
    try {
      LoaderService.show();
      const response = await GetPendingVerifications({
        limit: pendingPerPage,
        offset: (page - 1) * pendingPerPage,
        search: search.trim() || undefined,
        city: city.trim() || undefined,
        state: stateFilter.trim() || undefined,
        status: status.trim() || undefined,
        score_min: scoreMin ? Number(scoreMin) : undefined,
        score_max: scoreMax ? Number(scoreMax) : undefined,
      });
      if (response.status === "success" || response.status === true) {
        const verifications = Array.isArray(response.data)
          ? response.data
          : response.data?.verifications || response.data?.data || [];
        const total =
          response.count ??
          response.total ??
          (Array.isArray(response.data)
            ? response.data.length
            : response.data?.total || response.data?.count || 0);
        setPendingVerifications(verifications);
        setTotalPendingCount(total);
      }
    } catch (error) {
      toast.error("Failed to fetch pending verifications");
    } finally {
      LoaderService.hide();
    }
  }, []);

  useEffect(() => {
    fetchPendingVerifications(
      pendingPage,
      pendingSearch,
      pendingCity,
      pendingStateFilter,
      pendingStatus,
      pendingScoreMin,
      pendingScoreMax,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPage, filterKey]);

  useEffect(() => {
    fetchPublishJobs();
  }, [jobPage, jobStatusFilter, jobServiceFilter, sortBy, sortOrder]);

  const handleApplyPendingFilters = () => {
    setPendingPage(1);
    setFilterKey((k) => k + 1);
  };

  const handleClearPendingFilters = () => {
    setPendingSearch("");
    setPendingCity("");
    setPendingStateFilter("");
    setPendingStatus("");
    setPendingScoreMin("");
    setPendingScoreMax("");
    setPendingPage(1);
    setFilterKey((k) => k + 1);
  };

  const fetchJobDetail = async (jobId: number) => {
    try {
      LoaderService.show();
      const response = await GetJobDetails(jobId);
      if (response.status === "success" || response.status === true) {
        setSelectedJob(response.data);
      } else {
        toast.error(response.message || "Failed to fetch job details");
      }
    } catch (error) {
      toast.error("Error fetching job details");
    } finally {
      LoaderService.hide();
    }
  };

  const fetchPublishJobs = async () => {
    try {
      LoaderService.show();
      const response = await GetPublishMultipleService({
        limit: jobsPerPage,
        offset: (jobPage - 1) * jobsPerPage,
        status: jobStatusFilter || undefined,
        service_name: jobServiceFilter || undefined,
        user_id: jobUserIdSearch.trim() || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      if (response.status === "success" || response.status === true) {
        setPublishJobs(response.data.jobs);
        setTotalJobs(response.data.total);
      }
    } catch (error) {
      toast.error("Failed to fetch publish jobs");
    } finally {
      LoaderService.hide();
    }
  };

  const handleJobSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setJobPage(1);
    fetchPublishJobs();
  };

  const handleRowClick = (userId: string) => {
    setUserIdInput(userId);
  };

  const handleTriggerServices = async (approve: boolean, userId?: string) => {
    const targetUserId = userId || userIdInput.trim();
    if (!targetUserId) {
      toast.error("Please enter a User ID");
      return;
    }

    if (!approve) {
      const confirmed = window.confirm(
        `Are you sure you want to REJECT and DELETE user ${targetUserId}? This action cannot be undone.`,
      );
      if (!confirmed) return;
    }

    try {
      LoaderService.show();
      setTriggeredRows((prev) => new Set(prev).add(targetUserId));

      const response = await PostTriggerServices(targetUserId, approve);
      if (response.status === "success" || response.status === true) {
        toast.success(
          response.message ||
            (approve
              ? "User approved and services triggered"
              : "User rejected and deleted"),
        );
        if (!userId) setUserIdInput("");
        fetchPendingVerifications(
          pendingPage,
          pendingSearch,
          pendingCity,
          pendingStateFilter,
          pendingStatus,
          pendingScoreMin,
          pendingScoreMax,
        );
      } else {
        toast.error(response.message || "Operation failed");
        setTriggeredRows((prev) => {
          const newSet = new Set(prev);
          newSet.delete(targetUserId);
          return newSet;
        });
      }
    } catch (error) {
      console.error("Operation failed:", error);
      toast.error("Failed to process request");
      setTriggeredRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(targetUserId);
        return newSet;
      });
    } finally {
      LoaderService.hide();
    }
  };

  const handleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(key);
      setSortOrder("desc");
    }
    setJobPage(1);
  };

  const handleTriggerService = async (forceRun = false) => {
    const uid = serviceUserId.trim();
    if (!uid) {
      toast.error("Please enter a User ID");
      return;
    }

    const cid = serviceCandidateId.trim();
    if (dualParamServices.includes(selectedService) && !cid) {
      toast.error(`${selectedService} requires both User ID and Candidate ID`);
      return;
    }

    const payload: any = { service_name: selectedService, user_id: uid };
    if (dualParamServices.includes(selectedService) && cid) {
      payload.candidate_id = cid;
    }

    // Run compatibility check for dual-param services if not forced
    if (!forceRun && dualParamServices.includes(selectedService) && cid) {
      try {
        const check = await GetCompatibilityCheck(uid, cid);
        if (check?.status === "success" && !check.compatible && check.mismatches?.length > 0) {
          setCompatibilityWarning({
            mismatches: check.mismatches,
            user: check.data.user,
            candidate: check.data.candidate,
            pendingPayload: payload,
          });
          return;
        }
      } catch {
        // If check fails, proceed anyway
      }
    }

    try {
      LoaderService.show();
      const response = await PostServiceJob(payload);
      console.log("Service Job Response:", response);

      const isSuccess =
        response?.statusCode === 200 ||
        response?.body?.success === true ||
        response?.status === "success" ||
        response?.success === true;

      if (isSuccess) {
        const message = response?.body?.message || response?.message || "Service job created successfully";
        toast.success(message);
        setServiceUserId("");
        setServiceCandidateId("");
        setCompatibilityWarning(null);
        fetchPublishJobs();
      } else {
        toast.error(response?.body?.message || response?.message || "Failed to create service job");
      }
    } catch (error: any) {
      console.error("Service trigger failed:", error);
      toast.error(error.message || "Failed to trigger service");
    } finally {
      LoaderService.hide();
    }
  };

  const handleRestartJob = async (job: PublishJob) => {
    if (restartingJobId === job.id) return;
    const uid = job.user_id?.trim();
    if (!uid) {
      toast.error("Cannot restart: missing User ID");
      return;
    }

    const payload: any = {
      service_name: job.service_name,
      user_id: uid,
    };

    const cid = job.candidate_user_id?.trim();
    if (dualParamServices.includes(job.service_name)) {
      if (!cid) {
        toast.error(`Cannot restart ${job.service_name}: missing Candidate ID`);
        return;
      }
      payload.candidate_id = cid;
    }

    setServiceUserId(job.user_id || "");
    setServiceCandidateId(job.candidate_user_id || "");
    setRestartingJobId(job.id);
    LoaderService.show();
    try {
      const response = await PostServiceJob(payload);
      const isSuccess =
        response?.statusCode === 200 ||
        response?.body?.success === true ||
        response?.status === "success" ||
        response?.success === true;

      if (isSuccess) {
        toast.success(`Restarted ${job.service_name} for ${uid}`);
        fetchPublishJobs();
      } else {
        const errorMessage =
          response?.body?.message || response?.message || "Restart failed";
        toast.error(errorMessage);
      }
    } catch (error: any) {
      console.error("Restart failed:", error);
      toast.error(error?.message || "Restart request failed");
    } finally {
      setRestartingJobId(null);
      LoaderService.hide();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
      case "failed":
        return "bg-rose-500/20 text-rose-400 border-rose-500/30";
      case "pending":
        return "bg-amber-500/20 text-amber-400 border-amber-500/30";
      case "processing":
        return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      default:
        return "bg-slate-500/20 text-slate-400 border-slate-500/30";
    }
  };

  const canRestartJob = (status: string) => {
    const normalizedStatus = status?.toLowerCase();
    return normalizedStatus === "completed" || normalizedStatus === "failed";
  };

  return (
    <div className="mt-8 space-y-8 text-white p-6">
      <div className="max-w-7xl mx-auto space-y-12">
        {/* CASTE VERIFICATION SECTION */}
        <section>
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-black text-white italic uppercase tracking-wider flex items-center gap-3">
              <div className="p-2 bg-indigo-500/20 rounded-xl text-indigo-400 border border-indigo-500/20 shadow-inner">
                <UserCheck size={24} />
              </div>
              Caste Verification
            </h1>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Input Panel */}
            <div className="lg:col-span-1">
              <div className="bg-slate-900 border border-slate-800 rounded-[2rem] p-8 shadow-2xl h-full flex flex-col">
                <h2 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 mb-6 flex items-center gap-2">
                  <Play size={10} className="text-indigo-500" />
                  Manual Processor
                </h2>

                <div className="space-y-6 flex-grow">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                      User Identification
                    </label>
                    <input
                      type="text"
                      placeholder="ENTER UID (e.g. U123456)"
                      value={userIdInput}
                      onChange={(e) =>
                        setUserIdInput(e.target.value.toUpperCase())
                      }
                      className="w-full px-5 py-4 bg-slate-950 border border-slate-800 text-white rounded-2xl focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all placeholder:text-slate-800 font-mono text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => handleTriggerServices(true)}
                      className="group relative h-16 bg-emerald-600/10 hover:bg-emerald-600 text-emerald-400 hover:text-white border border-emerald-500/20 rounded-2xl font-black uppercase text-[10px] tracking-[0.2em] transition-all overflow-hidden flex items-center justify-center gap-2"
                    >
                      <CheckCircle size={16} />
                      APPROVE
                    </button>
                    <button
                      onClick={() => handleTriggerServices(false)}
                      className="group relative h-16 bg-rose-600/10 hover:bg-rose-600 text-rose-400 hover:text-white border border-rose-500/20 rounded-2xl font-black uppercase text-[10px] tracking-[0.2em] transition-all overflow-hidden flex items-center justify-center gap-2"
                    >
                      <AlertCircle size={16} />
                      REJECT
                    </button>
                  </div>
                </div>

                <div className="mt-8 p-4 bg-amber-500/10 border border-amber-500/20 rounded-2xl">
                  <p className="text-[8px] font-black text-amber-500 uppercase tracking-widest leading-relaxed">
                    NOTICE: Rejecting a user will permanently purge their node
                    from the active registry.
                  </p>
                </div>
              </div>
            </div>

            {/* List Panel */}
            <div className="lg:col-span-2">
              <div className="bg-slate-900 border border-slate-800 rounded-[2rem] p-8 shadow-2xl">
                <div className="flex justify-between items-center mb-8">
                  <div>
                    <h2 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 flex items-center gap-2">
                      <Clock size={10} className="text-amber-500" />
                      Pending Queue
                    </h2>
                    <p className="text-white text-xl font-black italic uppercase mt-1">
                      Caste Verification List ({pendingVerifications.length})
                    </p>
                  </div>
                  <button
                    onClick={() => setFilterKey((k) => k + 1)}
                    className="p-3 bg-slate-800 hover:bg-slate-700 rounded-2xl text-slate-400 transition-all active:scale-90"
                  >
                    <RefreshCw size={18} />
                  </button>
                </div>

                <div className="mb-6 rounded-3xl border border-slate-800 bg-slate-950/80 p-5 shadow-inner">
                  <div className="grid gap-4 xl:grid-cols-5">
                    <div className="xl:col-span-2">
                      <label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 mb-2 block">
                        Search
                      </label>
                      <input
                        type="text"
                        placeholder="User ID or Name"
                        value={pendingSearch}
                        onChange={(e) => setPendingSearch(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 text-white rounded-2xl focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 mb-2 block">
                        City
                      </label>
                      <input
                        type="text"
                        placeholder="City"
                        value={pendingCity}
                        onChange={(e) => setPendingCity(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 text-white rounded-2xl focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 mb-2 block">
                        State
                      </label>
                      <input
                        type="text"
                        placeholder="State"
                        value={pendingStateFilter}
                        onChange={(e) => setPendingStateFilter(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 text-white rounded-2xl focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-sm"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 mb-2 block">
                          Min Score
                        </label>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          placeholder="Min"
                          value={pendingScoreMin}
                          onChange={(e) => setPendingScoreMin(e.target.value)}
                          className="w-full px-4 py-3 bg-slate-900 border border-slate-800 text-white rounded-2xl focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 mb-2 block">
                          Max Score
                        </label>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          placeholder="Max"
                          value={pendingScoreMax}
                          onChange={(e) => setPendingScoreMax(e.target.value)}
                          className="w-full px-4 py-3 bg-slate-900 border border-slate-800 text-white rounded-2xl focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 mb-2 block">
                        Status
                      </label>
                      <select
                        value={pendingStatus}
                        onChange={(e) => setPendingStatus(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 text-white rounded-2xl focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-sm"
                      >
                        <option value="">All Statuses</option>
                        <option value="Requires_Admin_Review">Requires Admin Review</option>
                        <option value="Possibly_Maheshwari">Possibly Maheshwari</option>
                        <option value="Low_Profile_Confidence_Maheshwari">Low Profile Confidence</option>
                        <option value="Manual_Review_Maheshwari">Manual Review</option>
                        <option value="Community_Mismatch_Maheshwari">Community Mismatch</option>
                        <option value="Incomplete_Profile">Incomplete Profile</option>
                        <option value="Profile_Not_Found_For_Verification">Profile Not Found</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-5 flex flex-wrap items-center gap-3 justify-between">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500">
                      Narrow the list by user, geography, score or status
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={handleClearPendingFilters}
                        className="px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded-2xl text-slate-300 border border-slate-700 transition-all"
                      >
                        Clear
                      </button>
                      <button
                        onClick={handleApplyPendingFilters}
                        className="px-4 py-3 bg-indigo-600 hover:bg-indigo-500 rounded-2xl text-white font-black uppercase tracking-[0.2em] transition-all"
                      >
                        Apply Filters
                      </button>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto custom-scrollbar">
                  <table className="w-full text-left border-separate border-spacing-y-3">
                    <thead>
                      <tr className="text-[9px] font-black text-slate-500 uppercase tracking-widest">
                        <th className="px-6 pb-2">Principal</th>
                        <th className="px-6 pb-2">Geography</th>
                        <th className="px-6 pb-2">Score</th>
                        <th className="px-6 pb-2 text-right">Operation</th>
                      </tr>
                    </thead>
                    <tbody className="space-y-4">
                      {pendingVerifications.map((verification) => {
                        const isTriggered = triggeredRows.has(
                          verification.UserId,
                        );
                        const status = (
                          verification.Status || ""
                        ).toLowerCase();
                        const isApproved = status.includes("approved");
                        const isDisabled = isTriggered || isApproved;

                        return (
                          <tr
                            key={verification.UserId}
                            className={`group bg-slate-950/50 border border-slate-800/50 rounded-2xl transition-all hover:bg-slate-900 ${
                              isDisabled ? "opacity-30" : ""
                            }`}
                            onClick={() =>
                              !isDisabled && handleRowClick(verification.UserId)
                            }
                          >
                            <td className="px-6 py-4 rounded-l-2xl">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center text-indigo-400 text-xs font-black">
                                  {verification.UserId.substring(0, 2)}
                                </div>
                                <div>
                                  <p className="text-xs font-black text-white hover:text-indigo-400 transition-colors uppercase">
                                    {verification.Name}
                                  </p>
                                  <p className="text-[9px] font-mono text-slate-600 tracking-tighter">
                                    {verification.UserId}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-[10px] text-slate-400 font-bold uppercase">
                                {verification.City}
                              </p>
                              <p className="text-[8px] text-slate-600 font-black tracking-widest uppercase">
                                {verification.State}
                              </p>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <div className="h-1.5 w-12 bg-slate-800 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-amber-500"
                                    style={{
                                      width: `${Math.min(100, verification.MaheshwariScore)}%`,
                                    }}
                                  ></div>
                                </div>
                                <span className="text-[10px] font-black text-amber-500 italic">
                                  {verification.MaheshwariScore}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-right rounded-r-2xl">
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTriggerServices(
                                      true,
                                      verification.UserId,
                                    );
                                  }}
                                  disabled={isDisabled}
                                  className="p-2 bg-emerald-500/10 hover:bg-emerald-500 text-emerald-400 hover:text-white rounded-lg transition-all"
                                >
                                  <CheckCircle size={14} />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTriggerServices(
                                      false,
                                      verification.UserId,
                                    );
                                  }}
                                  disabled={isDisabled}
                                  className="p-2 bg-rose-500/10 hover:bg-rose-500 text-rose-400 hover:text-white rounded-lg transition-all"
                                >
                                  <AlertCircle size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {pendingVerifications.length === 0 && (
                    <div className="text-center py-12">
                      <Clock
                        size={48}
                        className="mx-auto text-slate-800 mb-4"
                      />
                      <p className="text-[10px] font-black text-slate-600 uppercase tracking-widest">
                        No spectral signatures awaiting validation
                      </p>
                    </div>
                  )}
                </div>

                {/* Pagination for Pending Verifications */}
                {totalPendingCount > 0 && (
                  <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-4 pt-6 border-t border-slate-800/50">
                    <p className="text-[10px] font-black text-slate-600 uppercase tracking-widest">
                      Signatures{" "}
                      {Math.min(
                        totalPendingCount,
                        (pendingPage - 1) * pendingPerPage + 1,
                      )}{" "}
                      -{" "}
                      {Math.min(
                        totalPendingCount,
                        pendingPage * pendingPerPage,
                      )}{" "}
                      of {totalPendingCount} detected
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() =>
                            setPendingPage(Math.max(1, pendingPage - 1))
                          }
                          disabled={pendingPage === 1}
                          className="p-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg text-slate-400 transition-all border border-slate-700/50"
                        >
                          <ChevronLeft size={16} />
                        </button>

                        <div className="flex items-center gap-1">
                          {[
                            ...Array(
                              Math.ceil(totalPendingCount / pendingPerPage),
                            ),
                          ]
                            .map((_, i) => {
                              const p = i + 1;
                              const isCurrent = p === pendingPage;
                              // Show only limited page numbers
                              if (
                                Math.abs(p - pendingPage) > 1 &&
                                p !== 1 &&
                                p !==
                                  Math.ceil(totalPendingCount / pendingPerPage)
                              ) {
                                if (
                                  p === 2 ||
                                  p ===
                                    Math.ceil(
                                      totalPendingCount / pendingPerPage,
                                    ) -
                                      1
                                )
                                  return (
                                    <span key={p} className="text-slate-700">
                                      ...
                                    </span>
                                  );
                                return null;
                              }
                              return (
                                <button
                                  key={p}
                                  onClick={() => setPendingPage(p)}
                                  className={`w-8 h-8 rounded-lg text-[10px] font-black transition-all ${
                                    isCurrent
                                      ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/20"
                                      : "bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white"
                                  }`}
                                >
                                  {p}
                                </button>
                              );
                            })
                            .filter(Boolean)
                            .reduce((acc: any[], curr: any, i, arr) => {
                              // Deduplicate ellipses
                              if (
                                curr &&
                                curr.type === "span" &&
                                acc.length > 0 &&
                                acc[acc.length - 1].type === "span"
                              )
                                return acc;
                              return [...acc, curr];
                            }, [])}
                        </div>

                        <button
                          onClick={() =>
                            setPendingPage(
                              Math.min(
                                Math.ceil(totalPendingCount / pendingPerPage),
                                pendingPage + 1,
                              ),
                            )
                          }
                          disabled={
                            pendingPage >=
                            Math.ceil(totalPendingCount / pendingPerPage)
                          }
                          className="p-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg text-slate-400 transition-all border border-slate-700/50"
                        >
                          <ChevronRight size={16} />
                        </button>
                      </div>

                      <div className="flex items-center gap-2 border-l border-slate-800 pl-4 h-8 text-[10px] font-black uppercase tracking-widest text-slate-500">
                        <span>Jump:</span>
                        <select
                          value={pendingPage}
                          onChange={(e) =>
                            setPendingPage(Number(e.target.value))
                          }
                          className="bg-slate-950 border border-slate-800 text-indigo-400 px-2 py-1 rounded-lg focus:outline-none transition-all cursor-pointer"
                        >
                          {Array.from(
                            {
                              length: Math.ceil(
                                totalPendingCount / pendingPerPage,
                              ),
                            },
                            (_, i) => i + 1,
                          ).map((p) => (
                            <option key={p} value={p}>
                              Page {p}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* SERVICE TRIGGER SECTION */}
        <section>
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-black text-white italic uppercase tracking-wider flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-xl text-emerald-400 border border-emerald-500/20 shadow-inner">
                <Play size={24} />
              </div>
              Trigger Service Job
            </h1>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-[2rem] p-8 shadow-2xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Service Selection Panel */}
              <div className="space-y-6">
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1 flex items-center gap-2">
                    <Layers size={12} className="text-emerald-500" />
                    Select Service
                  </label>
                  <select
                    value={selectedService}
                    onChange={(e) => setSelectedService(e.target.value)}
                    className="w-full px-5 py-4 bg-slate-950 border border-slate-800 text-white rounded-2xl focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 transition-all text-sm font-mono appearance-none cursor-pointer"
                  >
                    <optgroup label="Single User Services (UID only)">
                      <option value="common_service">
                        common_service (Full Pipeline)
                      </option>
                      <option value="matchmaking_all">
                        matchmaking_all (Run Matchmaking)
                      </option>
                      <option value="matchmaking_queue_all">
                        matchmaking_queue_all (Smart Queue)
                      </option>
                      <option value="tag_extraction">
                        tag_extraction (Extract Tags)
                      </option>
                      <option value="about_me_generation">
                        about_me_generation (Generate About Me)
                      </option>
                      <option value="whatsapp_pdf_template">
                        whatsapp_pdf_template (Send PDF)
                      </option>
                      <option value="question_tags">
                        question_tags (Process Tags)
                      </option>
                    </optgroup>
                    <optgroup label="Two User Services (UID + CID)">
                      <option value="force_match">
                        force_match (Force Match 2 Users)
                      </option>
                      <option value="hard_match">
                        hard_match (Queue + Force Match)
                      </option>
                      <option value="publish_match">
                        publish_match (Publish Match)
                      </option>
                      <option value="matchmaking_queue_single">
                        matchmaking_queue_single (Queue Single Match)
                      </option>
                    </optgroup>
                  </select>
                </div>

                {/* Service Description */}
                <div className="p-4 bg-slate-950/50 border border-slate-800 rounded-2xl">
                  <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-2">
                    Service Info
                  </p>
                  <p className="text-[11px] text-slate-400 leading-relaxed">
                    {selectedService === "common_service" &&
                      "Full pipeline: About Me → Tags → Matching (LLM + Astrology)"}
                    {selectedService === "force_match" &&
                      "Force create match between 2 users with astrology"}
                    {selectedService === "hard_match" &&
                      "Run queue-single and then force-match for the selected UID and CID"}
                    {selectedService === "matchmaking_all" &&
                      "Run actual matchmaking without queue"}
                    {selectedService === "matchmaking_queue_all" &&
                      "Run only smart queue matching"}
                    {selectedService === "matchmaking_queue_single" &&
                      "Process queue for specific UID and CID with reverse queue"}
                    {selectedService === "tag_extraction" &&
                      "Extract and allocate tags for user"}
                    {selectedService === "about_me_generation" &&
                      'Generate "About Me" via LLM'}
                    {selectedService === "publish_match" &&
                      "Publish existing match with astrology"}
                    {selectedService === "whatsapp_pdf_template" &&
                      "Send WhatsApp PDF to user"}
                    {selectedService === "question_tags" &&
                      "Process question tags for user"}
                  </p>
                </div>
              </div>

              {/* Input Panel */}
              <div className="space-y-6">
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                    User ID (Required)
                  </label>
                  <input
                    type="text"
                    placeholder="ENTER UID (e.g. U123456)"
                    value={serviceUserId}
                    onChange={(e) =>
                      setServiceUserId(e.target.value.toUpperCase())
                    }
                    className="w-full px-5 py-4 bg-slate-950 border border-slate-800 text-white rounded-2xl focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 transition-all placeholder:text-slate-800 font-mono text-sm"
                  />
                </div>

                {/* Conditional Candidate ID field */}
                {(selectedService === "force_match" ||
                  selectedService === "hard_match" ||
                  selectedService === "publish_match" ||
                  selectedService === "matchmaking_queue_single") && (
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1 flex items-center gap-2">
                      Candidate ID (Required)
                      <span className="px-2 py-0.5 bg-amber-500/20 text-amber-500 rounded text-[8px]">
                        DUAL PARAM
                      </span>
                    </label>
                    <input
                      type="text"
                      placeholder="ENTER CID (e.g. U789012)"
                      value={serviceCandidateId}
                      onChange={(e) =>
                        setServiceCandidateId(e.target.value.toUpperCase())
                      }
                      className="w-full px-5 py-4 bg-slate-950 border border-slate-800 text-white rounded-2xl focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 transition-all placeholder:text-slate-800 font-mono text-sm"
                    />
                  </div>
                )}

                <button
                  onClick={() => handleTriggerService()}
                  className="w-full h-14 bg-emerald-600 hover:bg-emerald-500 text-white border border-emerald-500/20 rounded-2xl font-black uppercase text-[11px] tracking-[0.2em] transition-all flex items-center justify-center gap-3 shadow-lg shadow-emerald-500/20"
                >
                  <Send size={18} />
                  Trigger Service Job
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* SERVICE STATUS SECTION */}
        <section>
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-black text-white italic uppercase tracking-wider flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-xl text-amber-400 border border-amber-500/20 shadow-inner">
                <Send size={24} />
              </div>
              Service Job Status
            </h1>
            <div className="flex gap-4">
              <div className="relative">
                <select
                  value={jobStatusFilter}
                  onChange={(e) => {
                    setJobStatusFilter(e.target.value);
                    setJobPage(1);
                  }}
                  className="h-11 bg-slate-900 border border-slate-800 text-white text-[10px] font-black uppercase tracking-widest px-10 rounded-2xl focus:outline-none focus:ring-1 focus:ring-amber-500 transition-all appearance-none cursor-pointer"
                >
                  <option value="">Status: All Nodes</option>
                  <option value="pending">Node: Pending</option>
                  <option value="processing">Node: Processing</option>
                  <option value="completed">Node: Completed</option>
                  <option value="failed">Node: Failed</option>
                </select>
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none">
                  <Clock size={14} />
                </div>
              </div>
              <div className="relative">
                <select
                  value={jobServiceFilter}
                  onChange={(e) => {
                    setJobServiceFilter(e.target.value);
                    setJobPage(1);
                  }}
                  className="h-11 bg-slate-900 border border-slate-800 text-white text-[10px] font-black uppercase tracking-widest px-10 rounded-2xl focus:outline-none focus:ring-1 focus:ring-amber-500 transition-all appearance-none cursor-pointer"
                >
                  <option value="">Service: All Services</option>
                  <option value="publish_match">publish_match</option>
                  <option value="matchmaking_all">matchmaking_all</option>
                  <option value="force_match">force_match</option>
                  <option value="hard_match">hard_match</option>
                  <option value="common_service">common_service</option>
                </select>
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none">
                  <Layers size={14} />
                </div>
              </div>
              <form onSubmit={handleJobSearch} className="relative">
                <input
                  type="text"
                  placeholder="UID SEARCH..."
                  value={jobUserIdSearch}
                  onChange={(e) =>
                    setJobUserIdSearch(e.target.value.toUpperCase())
                  }
                  className="h-11 w-48 bg-slate-900 border border-slate-800 text-white text-[10px] font-black uppercase tracking-widest px-10 rounded-2xl focus:outline-none focus:ring-1 focus:ring-amber-500 transition-all placeholder:text-slate-700"
                />
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none">
                  <Search size={14} />
                </div>
                <button type="submit" className="hidden"></button>
              </form>
              <button
                onClick={fetchPublishJobs}
                className="p-3 bg-slate-800 hover:bg-amber-500/10 hover:text-amber-400 rounded-2xl text-slate-400 border border-transparent hover:border-amber-500/20 transition-all active:scale-90"
              >
                <RefreshCw size={18} />
              </button>
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-[2rem] p-8 shadow-2xl relative overflow-hidden">
            {/* Background Decoration */}
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-amber-500/5 blur-[100px] rounded-full pointer-events-none"></div>

            <div className="overflow-x-auto custom-scrollbar">
              <table className="w-full text-left border-separate border-spacing-y-3">
                <thead>
                  <tr className="text-[9px] font-black text-slate-500 uppercase tracking-widest">
                    <th
                      className="px-6 pb-2 cursor-pointer hover:text-white transition-colors"
                      onClick={() => handleSort("id")}
                    >
                      Job Index{" "}
                      {sortBy === "id" && (sortOrder === "asc" ? "↑" : "↓")}
                    </th>
                    <th className="px-6 pb-2">Entities Involved</th>
                    {/* SIGNAL METRICS COLUMN - HIDDEN
                      <th className="px-6 pb-2 cursor-pointer hover:text-white transition-colors" onClick={() => handleSort('priority')}>
                        Signal Metrics {sortBy === 'priority' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </th>
                      */}
                    <th
                      className="px-6 pb-2 cursor-pointer hover:text-white transition-colors"
                      onClick={() => handleSort("created_at")}
                    >
                      Temporal Map{" "}
                      {sortBy === "created_at" &&
                        (sortOrder === "asc" ? "↑" : "↓")}
                    </th>
                    <th
                      className="px-6 pb-2 cursor-pointer hover:text-white transition-colors"
                      onClick={() => handleSort("status")}
                    >
                      Node Status{" "}
                      {sortBy === "status" && (sortOrder === "asc" ? "↑" : "↓")}
                    </th>
                    <th className="px-6 pb-2 text-right">Data View</th>
                  </tr>
                </thead>
                <tbody>
                  {publishJobs.map((job) => (
                    <tr
                      key={job.id}
                      className="group bg-slate-950/50 border border-slate-800/50 rounded-2xl transition-all hover:bg-slate-900 transform hover:-translate-y-0.5"
                    >
                      <td className="px-6 py-4 rounded-l-2xl">
                        <span className="text-[10px] font-mono text-slate-600 font-black">
                          #{job.id}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex -space-x-2">
                            <div className="w-7 h-7 rounded-full bg-amber-500/10 border border-slate-800 flex items-center justify-center text-[10px] font-black text-amber-500">
                              U
                            </div>
                            <div className="w-7 h-7 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-[10px] font-black text-slate-500">
                              C
                            </div>
                          </div>
                          <div>
                            <p className="text-[10px] font-black text-white hover:text-amber-400 transition-colors tracking-tighter">
                              {job.user_id}{" "}
                              <span className="text-slate-700 mx-1">→</span>{" "}
                              {job.candidate_user_id}
                            </p>
                            <p className="text-[8px] font-black text-slate-600 uppercase tracking-widest mt-0.5">
                              {job.service_name}
                            </p>
                          </div>
                        </div>
                      </td>
                      {/* SIGNAL METRICS DATA - HIDDEN
                         <td className="px-6 py-4">
                            <div className="flex items-center gap-3 text-[10px] font-mono font-bold">
                               <div className="flex flex-col">
                                  <span className="text-amber-500/50 text-[8px] uppercase font-black">LLM</span>
                                  <span className="text-white">{job.payload?.llm_score || '0.0'}</span>
                               </div>
                               <div className="w-px h-4 bg-slate-800"></div>
                               <div className="flex flex-col">
                                  <span className="text-blue-500/50 text-[8px] uppercase font-black">GN</span>
                                  <span className="text-white">{job.payload?.gunn_score || '0'}</span>
                               </div>
                               {job.payload?.both && (
                                  <div className="px-1.5 py-0.5 bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 rounded text-[7px] uppercase font-black">Bi</div>
                               )}
                            </div>
                         </td>
                         */}
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                          <p className="text-[9px] text-slate-400 font-bold">
                            {new Date(job.created_at).toLocaleDateString()}
                          </p>
                          <p className="text-[8px] text-slate-700 font-black tabular-nums">
                            {new Date(job.created_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1.5">
                          <span
                            className={`w-fit px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest border ${getStatusColor(job.status)}`}
                          >
                            {job.status}
                          </span>
                          {job.error_msg && (
                            <p
                              className="text-[8px] text-rose-500 font-bold max-w-[150px] truncate italic"
                              title={job.error_msg}
                            >
                              {job.error_msg}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right rounded-r-2xl flex items-center justify-end gap-2">
                        {canRestartJob(job.status) && (
                          <button
                            onClick={() => handleRestartJob(job)}
                            disabled={restartingJobId === job.id}
                            className="p-2 bg-cyan-500/10 hover:bg-cyan-500 text-cyan-300 hover:text-white rounded-lg transition-all active:scale-90 disabled:opacity-40 disabled:cursor-not-allowed"
                            title={
                              job.status?.toLowerCase() === "failed"
                                ? "Re-run failed job"
                                : "Re-run completed job"
                            }
                          >
                            <RefreshCw
                              size={16}
                              className={
                                restartingJobId === job.id ? "animate-spin" : ""
                              }
                            />
                          </button>
                        )}
                        <button
                          onClick={() => fetchJobDetail(job.id)}
                          className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white rounded-lg transition-all active:scale-90"
                        >
                          <Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-[10px] font-black text-slate-600 uppercase tracking-widest">
                Mapping {Math.min(totalJobs, (jobPage - 1) * jobsPerPage + 1)} -{" "}
                {Math.min(totalJobs, jobPage * jobsPerPage)} of {totalJobs}{" "}
                signatures detected
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setJobPage(Math.max(1, jobPage - 1))}
                    disabled={jobPage === 1}
                    className="p-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg text-slate-400 transition-all border border-slate-700/50"
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <div className="flex items-center gap-1">
                    {[...Array(Math.ceil(totalJobs / jobsPerPage))]
                      .map((_, i) => {
                        const p = i + 1;
                        const isCurrent = p === jobPage;
                        if (
                          Math.abs(p - jobPage) > 1 &&
                          p !== 1 &&
                          p !== Math.ceil(totalJobs / jobsPerPage)
                        ) {
                          if (
                            p === 2 ||
                            p === Math.ceil(totalJobs / jobsPerPage) - 1
                          )
                            return (
                              <span key={p} className="text-slate-700">
                                ...
                              </span>
                            );
                          return null;
                        }
                        return (
                          <button
                            key={p}
                            onClick={() => setJobPage(p)}
                            className={`w-7 h-7 rounded-lg text-[9px] font-black transition-all ${
                              isCurrent
                                ? "bg-amber-600 text-white shadow-lg shadow-amber-600/20"
                                : "bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white"
                            }`}
                          >
                            {p}
                          </button>
                        );
                      })
                      .filter(Boolean)
                      .reduce((acc: any[], curr: any) => {
                        if (
                          curr &&
                          curr.type === "span" &&
                          acc.length > 0 &&
                          acc[acc.length - 1].type === "span"
                        )
                          return acc;
                        return [...acc, curr];
                      }, [])}
                  </div>

                  <button
                    onClick={() =>
                      setJobPage(
                        Math.min(
                          Math.ceil(totalJobs / jobsPerPage),
                          jobPage + 1,
                        ),
                      )
                    }
                    disabled={jobPage >= Math.ceil(totalJobs / jobsPerPage)}
                    className="p-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg text-slate-400 transition-all border border-slate-700/50"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>

                <div className="flex items-center gap-2 border-l border-slate-800 pl-4 h-8 text-[10px] font-black uppercase tracking-widest text-slate-500">
                  <span>Jump:</span>
                  <select
                    value={jobPage}
                    onChange={(e) => setJobPage(Number(e.target.value))}
                    className="bg-slate-950 border border-slate-800 text-amber-500 px-2 py-1 rounded-lg focus:outline-none transition-all cursor-pointer"
                  >
                    {Array.from(
                      { length: Math.ceil(totalJobs / jobsPerPage) },
                      (_, i) => i + 1,
                    ).map((p) => (
                      <option key={p} value={p}>
                        Page {p}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Compatibility Mismatch Warning Modal */}
      {compatibilityWarning && (
        <div className="fixed inset-0 z-[600] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-slate-900 border border-amber-500/30 rounded-[2rem] w-full max-w-lg shadow-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-amber-500/20 rounded-xl text-amber-400 border border-amber-500/20">
                <AlertCircle size={22} />
              </div>
              <div>
                <h3 className="text-lg font-black text-white uppercase tracking-tight">Preference Mismatch Detected</h3>
                <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-0.5">Review before triggering service</p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              {/* User & Candidate summary */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 bg-slate-950/60 border border-slate-800 rounded-2xl">
                  <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest mb-1">User</p>
                  <p className="text-sm font-black text-white">{compatibilityWarning.user.name}</p>
                  <p className="text-[10px] text-slate-400 mt-1">Age: <span className="text-white font-bold">{compatibilityWarning.user.age ?? "N/A"}</span></p>
                  <p className="text-[10px] text-slate-400">Status: <span className="text-white font-bold">{compatibilityWarning.user.marital_status ?? "N/A"}</span></p>
                  {compatibilityWarning.user.gotra && (
                    <p className="text-[10px] text-slate-400">Gotra: <span className="text-white font-bold">{compatibilityWarning.user.gotra}</span></p>
                  )}
                  {compatibilityWarning.user.maternal_gotra && (
                    <p className="text-[10px] text-slate-400">Maternal Gotra: <span className="text-white font-bold">{compatibilityWarning.user.maternal_gotra}</span></p>
                  )}
                </div>
                <div className="p-4 bg-slate-950/60 border border-slate-800 rounded-2xl">
                  <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest mb-1">Candidate</p>
                  <p className="text-sm font-black text-white">{compatibilityWarning.candidate.name}</p>
                  <p className="text-[10px] text-slate-400 mt-1">Age: <span className="text-white font-bold">{compatibilityWarning.candidate.age ?? "N/A"}</span></p>
                  <p className="text-[10px] text-slate-400">Status: <span className="text-white font-bold">{compatibilityWarning.candidate.marital_status ?? "N/A"}</span></p>
                  {compatibilityWarning.candidate.gotra && (
                    <p className="text-[10px] text-slate-400">Gotra: <span className="text-white font-bold">{compatibilityWarning.candidate.gotra}</span></p>
                  )}
                  {compatibilityWarning.candidate.maternal_gotra && (
                    <p className="text-[10px] text-slate-400">Maternal Gotra: <span className="text-white font-bold">{compatibilityWarning.candidate.maternal_gotra}</span></p>
                  )}
                </div>
              </div>

              {/* Mismatch details */}
              <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-2xl space-y-2">
                <p className="text-[9px] font-black text-amber-500 uppercase tracking-widest">Mismatches Found</p>
                {compatibilityWarning.mismatches.map((m, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                    <p className="text-[11px] text-amber-300 leading-relaxed">{m.reason}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setCompatibilityWarning(null)}
                className="flex-1 h-12 bg-slate-800 hover:bg-slate-700 text-white rounded-2xl font-black uppercase text-[10px] tracking-[0.2em] transition-all border border-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setCompatibilityWarning(null);
                  handleTriggerService(true);
                }}
                className="flex-1 h-12 bg-amber-600 hover:bg-amber-500 text-white rounded-2xl font-black uppercase text-[10px] tracking-[0.2em] transition-all"
              >
                Continue Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payload Viewer Modal */}
      {selectedJob && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] w-full max-w-4xl shadow-2xl scale-in transform transition-all overflow-hidden max-h-[90vh] flex flex-col">
            <div className="p-8 border-b border-slate-800 flex justify-between items-center bg-slate-900/50">
              <div>
                <h3 className="text-xl font-black text-white italic uppercase tracking-tight">
                  Job Detail Node #{selectedJob.id}
                </h3>
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mt-1">
                  Full Execution Trace & Registry Audit
                </p>
              </div>
              <button
                onClick={() => setSelectedJob(null)}
                className="p-3 bg-slate-800 hover:bg-rose-500/10 hover:text-rose-500 rounded-2xl text-slate-500 transition-all active:scale-95"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
              {/* Header Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-5 bg-slate-950/50 border border-slate-800 rounded-2xl">
                  <p className="text-[8px] font-black text-slate-600 uppercase tracking-widest mb-2">
                    Service
                  </p>
                  <p className="text-sm font-black text-white italic truncate">
                    {selectedJob.service_name}
                  </p>
                </div>
                <div className="p-5 bg-slate-950/50 border border-slate-800 rounded-2xl">
                  <p className="text-[8px] font-black text-slate-600 uppercase tracking-widest mb-2">
                    Status
                  </p>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest border ${getStatusColor(selectedJob.status)}`}
                  >
                    {selectedJob.status}
                  </span>
                </div>
                <div className="p-5 bg-slate-950/50 border border-slate-800 rounded-2xl">
                  <p className="text-[8px] font-black text-slate-600 uppercase tracking-widest mb-2">
                    Priority
                  </p>
                  <p className="text-sm font-black text-amber-500">
                    {selectedJob.priority}
                  </p>
                </div>
                <div className="p-5 bg-slate-950/50 border border-slate-800 rounded-2xl">
                  <p className="text-[8px] font-black text-slate-600 uppercase tracking-widest mb-2">
                    Attempts
                  </p>
                  <p className="text-sm font-black text-white">
                    {selectedJob.attempt_statistics?.total_attempts || 0}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-5 bg-slate-950/50 border border-slate-800 rounded-2xl">
                  <p className="text-[8px] font-black text-slate-600 uppercase tracking-widest mb-2">
                    Source User
                  </p>
                  <p className="text-lg font-black text-white italic font-mono">
                    {selectedJob.user_id}
                  </p>
                </div>
                <div className="p-5 bg-slate-950/50 border border-slate-800 rounded-2xl">
                  <p className="text-[8px] font-black text-slate-600 uppercase tracking-widest mb-2">
                    Candidate User
                  </p>
                  <p className="text-lg font-black text-white italic font-mono">
                    {selectedJob.candidate_user_id || "N/A"}
                  </p>
                </div>
              </div>

              {/* Attempt Statistics */}
              {selectedJob.attempt_statistics && (
                <div className="p-6 bg-slate-950/20 border border-slate-800 rounded-2xl">
                  <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-4">
                    Attempt Distribution
                  </h4>
                  <div className="flex gap-8">
                    <div className="flex flex-col">
                      <span className="text-[8px] font-black text-emerald-500 uppercase">
                        Completed
                      </span>
                      <span className="text-xl font-black text-white">
                        {selectedJob.attempt_statistics.completed_attempts}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[8px] font-black text-rose-500 uppercase">
                        Failed
                      </span>
                      <span className="text-xl font-black text-white">
                        {selectedJob.attempt_statistics.failed_attempts}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[8px] font-black text-blue-500 uppercase">
                        Processing
                      </span>
                      <span className="text-xl font-black text-white">
                        {selectedJob.attempt_statistics.processing_attempts}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Retries Section */}
              {selectedJob.attempts && selectedJob.attempts.length > 0 && (
                <div className="space-y-4">
                  <h4 className="text-[10px] font-black text-indigo-400 uppercase tracking-widest flex items-center gap-2">
                    <RefreshCw size={12} />
                    Retries & Execution History
                  </h4>
                  <div className="bg-slate-950 rounded-2xl border border-slate-800 overflow-hidden">
                    <table className="w-full text-left text-[10px]">
                      <thead>
                        <tr className="bg-slate-900/50 text-slate-500 font-black uppercase tracking-widest">
                          <th className="px-4 py-3">#</th>
                          <th className="px-4 py-3">Worker</th>
                          <th className="px-4 py-3">Status</th>
                          <th className="px-4 py-3">Duration</th>
                          <th className="px-4 py-3">Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-900">
                        {selectedJob.attempts.map((attempt) => (
                          <tr
                            key={attempt.id}
                            className="hover:bg-white/5 transition-colors"
                          >
                            <td className="px-4 py-3 font-mono text-slate-600">
                              {attempt.attempt_no}
                            </td>
                            <td className="px-4 py-3 font-bold text-slate-400">
                              {attempt.worker_id}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`px-1.5 py-0.5 rounded text-[8px] font-black uppercase border ${getStatusColor(attempt.status)}`}
                              >
                                {attempt.status}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-white font-bold">
                                {attempt.finished_at
                                  ? `${((new Date(attempt.finished_at).getTime() - new Date(attempt.started_at).getTime()) / 1000).toFixed(1)}s`
                                  : "..."}
                              </p>
                              <p className="text-[8px] text-slate-700">
                                {new Date(
                                  attempt.started_at,
                                ).toLocaleTimeString()}
                              </p>
                            </td>
                            <td className="px-4 py-3">
                              {attempt.error_msg ? (
                                <p className="text-rose-400 italic leading-tight">
                                  {attempt.error_msg}
                                </p>
                              ) : (
                                <p className="text-emerald-500 font-black">
                                  SUCCESS
                                </p>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h4 className="text-[10px] font-black text-amber-500 uppercase tracking-widest">
                    Execution Payload
                  </h4>
                  <pre className="p-6 bg-slate-950 rounded-2xl border border-slate-800 text-[10px] font-mono text-emerald-400 overflow-x-auto custom-scrollbar max-h-64">
                    {JSON.stringify(selectedJob.payload, null, 2)}
                  </pre>
                </div>

                <div className="space-y-4">
                  <h4 className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">
                    Temporal Result Map
                  </h4>
                  <pre className="p-6 bg-slate-950 rounded-2xl border border-slate-800 text-[10px] font-mono text-indigo-400 overflow-x-auto custom-scrollbar max-h-64">
                    {JSON.stringify(
                      selectedJob.result || selectedJob.payload?.meta || {},
                      null,
                      2,
                    )}
                  </pre>
                </div>
              </div>

              {selectedJob.error_msg && (
                <div className="p-6 bg-rose-500/5 border border-rose-500/20 rounded-2xl space-y-2">
                  <h4 className="text-[10px] font-black text-rose-500 uppercase tracking-widest">
                    Root Failure Trace
                  </h4>
                  <p className="text-xs font-bold text-rose-300 italic leading-relaxed">
                    {selectedJob.error_msg}
                  </p>
                </div>
              )}
            </div>

            <div className="p-8 border-t border-slate-800 bg-slate-950 flex justify-between items-center">
              <div className="text-[9px] font-black text-slate-700 uppercase tracking-widest">
                Creation Timestamp:{" "}
                {new Date(selectedJob.created_at).toLocaleString()}
              </div>
              <button
                onClick={() => setSelectedJob(null)}
                className="px-8 py-3 bg-slate-800 hover:bg-slate-700 text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-xl transition-all active:scale-95"
              >
                Acknowledge
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminServices;
